import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Grid, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { NavItem } from "components/LeftNavV2/LeftNavDrawerV2";
import { useLandingPageSections } from "hooks/useLandingPageSections";
import { usePageTitle } from "hooks/usePageTitle";
import { useThemeMode } from "hooks/useThemeMode";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

export function LandingPage() {
  usePageTitle("Home");

  const { sections, loading } = useLandingPageSections();

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="stretch"
      sx={{
        minHeight: `calc(100vh - 116px)`,
        px: 3,
        py: 6,
        // Canvas + topo texture come from `HomeLinesBackdrop` (full viewport).
        backgroundColor: "transparent",
      }}
    >
      <Stack spacing={6}>
        {sections.map((section) => (
          <Section
            key={section.title}
            title={section.longTitle ?? section.title}
            items={section.items}
            loading={loading}
          />
        ))}
      </Stack>
    </Grid>
  );
}

function Section({ title, items, loading }: { title: string; items: NavItem[]; loading: boolean }) {
  const theme = useTheme();
  const { themeMode } = useThemeMode();
  // Dark tiles sit on the dark canvas; in light mode they inherit the page
  // background (the canvas tint is already painted by LandingPage behind them).
  const backgroundColor = themeMode === "dark" ? theme.palette.canvas : undefined;

  const tiles = useMemo(
    () => items.filter((item) => item.kind === "Standard" || item.kind === "Group"),
    [items]
  );

  return (
    <Stack spacing={1}>
      <Typography variant="overline">{title}</Typography>

      <Grid container spacing={2}>
        {loading
          ? Array.from({ length: 2 }).map((_, idx) => (
              <Grid key={`skeleton-${title}-${idx}`} size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  elevation={0}
                  sx={{ p: 3, height: "100%", borderColor: "divider" }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" width="70%" />
                  </Stack>
                </Paper>
              </Grid>
            ))
          : tiles.map((tile) => (
              <Grid key={tile.path} size={{ xs: 12, md: 6 }}>
                <RouterLink
                  to={`/${tile.path}`}
                  state={tile.kind === "Group" ? { expandGroupNav: tile.text } : undefined}
                  style={{ textDecoration: "none" }}
                >
                  <Paper
                    variant="outlined"
                    elevation={0}
                    sx={{
                      p: 3,
                      height: "100%",
                      position: "relative",
                      borderColor: "divider",
                      cursor: "pointer",
                      backgroundColor: backgroundColor,
                    }}
                  >
                    <Stack spacing={1}>
                      {tile.tileImage ? (
                        <Box
                          component="img"
                          src={
                            themeMode === "dark"
                              ? (tile.tileImageDark ?? tile.tileImage)
                              : tile.tileImage
                          }
                          alt=""
                          sx={{ width: 56, height: 56, objectFit: "contain" }}
                        />
                      ) : (
                        <tile.Icon color="disabled" fontSize="large" />
                      )}
                      <Typography variant="h6">{tile.text}</Typography>

                      <Typography variant="subtitle1" color="text.secondary" paddingRight={2}>
                        {tile.description}
                      </Typography>
                      <NavigateNextIcon
                        color="disabled"
                        sx={{
                          position: "absolute",
                          right: theme.spacing(2),
                          bottom: theme.spacing(2),
                        }}
                      />
                    </Stack>
                  </Paper>
                </RouterLink>
              </Grid>
            ))}
      </Grid>
    </Stack>
  );
}
